import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { Loader2, QrCode, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { todayISO } from "@/lib/format";

export const PUNCH_KEY = "ms_punch_k";
const hhmm = (iso) => new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });

const position = () => new Promise((res, rej) => {
  if (!navigator.geolocation) return rej(new Error("Geolocalizzazione non disponibile su questo telefono"));
  navigator.geolocation.getCurrentPosition(res, () => rej(new Error("Attiva la posizione (GPS) e consenti l'accesso all'app")),
    { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 });
});

const tokenFrom = (text) => { try { return new URL(text).searchParams.get("k"); } catch { return null; } };

function Scanner({ onToken }) {
  const ref = useRef(null);
  useEffect(() => {
    const s = new Html5Qrcode("punch-scanner");
    ref.current = s;
    s.start({ facingMode: "environment" }, { fps: 10, qrbox: 230 }, (txt) => {
      const k = tokenFrom(txt);
      if (k) { s.stop().catch(() => {}); onToken(k); }
    }).catch(() => {});
    return () => { if (s.isScanning) s.stop().catch(() => {}); };
  }, [onToken]);
  return <div id="punch-scanner" className="overflow-hidden rounded-2xl bg-black" data-testid="punch-scanner" />;
}

const REQ = { in_attesa: "In attesa", approvato: "Approvata", rifiutato: "Rifiutata" };

function ForgotRequest() {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(todayISO());
  const [time, setTime] = useState("08:00");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [reqs, setReqs] = useState([]);
  const load = () => api.get("/tech/punch-requests").then((r) => setReqs(r.data));
  useEffect(() => { load(); }, []);
  const send = async () => {
    setBusy(true);
    try {
      await api.post("/tech/punch-requests", { date, time, note });
      toast.success("Richiesta inviata all'ufficio");
      setOpen(false);
      setNote("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3 rounded-2xl border bg-white p-4" data-testid="punch-forgot">
      {open ? (
        <>
          <p className="text-sm font-semibold">Richiesta entrata dimenticata</p>
          <div className="grid grid-cols-2 gap-2">
            <Input type="date" value={date} max={todayISO()} onChange={(e) => setDate(e.target.value)} data-testid="punch-forgot-date" />
            <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="punch-forgot-time" />
          </div>
          <Textarea rows={2} placeholder="Motivo (es. telefono scarico)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="punch-forgot-note" />
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1 rounded-full" onClick={() => setOpen(false)}>Annulla</Button>
            <Button className="flex-1 rounded-full" disabled={busy || note.trim().length < 3} onClick={send} data-testid="punch-forgot-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Invia</Button>
          </div>
        </>
      ) : (
        <button className="w-full text-left text-sm font-semibold text-[#3d6b16]" onClick={() => setOpen(true)} data-testid="punch-forgot-open">Hai dimenticato di timbrare l'entrata? Chiedi all'ufficio</button>
      )}
      {reqs.length > 0 && (
        <div className="space-y-1 border-t pt-3 text-xs text-slate-600" data-testid="punch-forgot-list">
          {reqs.slice(0, 5).map((r) => <p key={r.id} className="flex justify-between"><span>{r.date} alle {r.time}</span><span className="font-semibold">{REQ[r.status]}</span></p>)}
        </div>
      )}
    </div>
  );
}

export default function TechPunch() {
  const [status, setStatus] = useState(null);
  const [scan, setScan] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const loadStatus = () => api.get("/tech/tracking/status").then((r) => setStatus(r.data));
  const punch = async (k) => {
    setScan(false);
    setBusy(true);
    setResult(null);
    try {
      const { coords } = await position();
      const { data } = await api.post("/tech/punch", { k, lat: coords.latitude, lng: coords.longitude, accuracy: coords.accuracy });
      setResult({ ok: true, ...data });
    } catch (e) {
      setResult({ ok: false, msg: e.response ? errMsg(e) : e.message });
    } finally {
      sessionStorage.removeItem(PUNCH_KEY);
      setBusy(false);
      loadStatus();
    }
  };

  useEffect(() => {
    loadStatus();
    const k = new URLSearchParams(window.location.search).get("k") || sessionStorage.getItem(PUNCH_KEY);
    if (k) punch(k);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const next = status?.clocked_in ? "uscita" : "entrata";
  return (
    <div className="space-y-5" data-testid="tech-punch-page">
      <div>
        <h1 className="font-display text-2xl font-bold">Timbratura</h1>
        <p className="text-sm text-slate-500" data-testid="punch-status">
          {status?.last_punch ? `Ultima: ${status.last_punch.type} alle ${hhmm(status.last_punch.at)}` : "Nessuna timbratura oggi"}
        </p>
      </div>
      {status && !status.consented && (
        <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-800" data-testid="punch-consent-needed">Per timbrare devi accettare la geolocalizzazione: torna all'<Link to="/tecnico" className="font-semibold underline">Agenda</Link> e tocca il riquadro della geolocalizzazione.</p>
      )}
      {result && (
        <div className={`flex items-start gap-3 rounded-2xl p-4 ${result.ok ? "bg-[#a2ca6b]/20 text-[#2f5410]" : "bg-red-50 text-red-700"}`} data-testid="punch-result">
          {result.ok ? <CheckCircle2 className="h-6 w-6 shrink-0" /> : <XCircle className="h-6 w-6 shrink-0" />}
          <p className="text-sm font-semibold">{result.ok ? `${result.type === "entrata" ? "Entrata" : "Uscita"} registrata alle ${hhmm(result.at)}${result.site_name ? ` · ${result.site_name}` : ""}` : result.msg}</p>
          {result.ok && result.report_pending?.length > 0 && (
            <div className="mt-2 space-y-1" data-testid="punch-report-reminder">
              {result.report_pending.map((r) => (
                <Link key={r.id} to={`/tecnico/intervento/${r.id}`} className="block rounded-xl bg-amber-100 px-3 py-2 text-sm font-semibold text-amber-900" data-testid={`punch-report-link-${r.id}`}>Compila il rapportino del cantiere {r.client} →</Link>
              ))}
            </div>
          )}
        </div>
      )}
      {scan ? <Scanner onToken={punch} /> : (
        <Button onClick={() => setScan(true)} disabled={busy} className="h-14 w-full rounded-2xl bg-[#61a229] text-base hover:bg-[#528a22]" data-testid="punch-scan-button">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <QrCode className="h-5 w-5" />} Inquadra il QR per timbrare l'{next}
        </Button>
      )}
      {scan && <Button variant="outline" className="w-full rounded-full" onClick={() => setScan(false)} data-testid="punch-scan-cancel">Annulla</Button>}
      <p className="text-xs text-slate-500">La timbratura è valida solo vicino alla sede. Puoi anche inquadrare il QR con la fotocamera del telefono.</p>
      <ForgotRequest />
    </div>
  );
}
